import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';

const SuperAdminLogin = () => {
  const { login, isAuthenticated } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPass, setShowPass] = useState(false);

  if (isAuthenticated) {
    navigate('/super-admin', { replace: true });
    return null;
  }

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address';
    if (!form.password.trim()) e.password = 'Password is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setErrors({});
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/super-admin');
    } catch (err) {
      const msg = err.response?.data?.message || '';
      if (msg.toLowerCase().includes('password') || msg.toLowerCase().includes('incorrect') || msg.toLowerCase().includes('invalid')) {
        setErrors({ password: 'Incorrect password. Please try again.' });
      } else if (msg.toLowerCase().includes('email') || msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('user')) {
        setErrors({ email: 'No account found with this email.' });
      } else {
        setErrors({ general: msg || 'Login failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    width: '100%',
    padding: '13px 16px',
    border: `1.5px solid ${errors[field] ? '#ef4444' : '#e2e8f0'}`,
    borderRadius: 12,
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    color: '#0f172a',
    background: errors[field] ? '#fef2f2' : '#f8fafc',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  });

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f1f5f9',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      padding: '24px 16px',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap');
        .login-card { animation: slideUp 0.4s cubic-bezier(0.16,1,0.3,1) both; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .login-btn:hover:not(:disabled) { background: #4f46e5 !important; transform: translateY(-1px); box-shadow: 0 8px 20px rgba(99,102,241,0.35) !important; }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
        .login-btn { transition: all 0.2s !important; }
        .show-pass:hover { color: #6366f1 !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="login-card" style={{
        width: '100%',
        maxWidth: 440,
        background: '#ffffff',
        borderRadius: 24,
        boxShadow: '0 4px 6px rgba(0,0,0,0.05), 0 24px 48px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
      }}>

        {/* Top accent bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)' }} />
	<div style={{ padding: '16px 40px 0' }}>
	  <button
	    onClick={() => navigate('/')}
	    style={{
	      background: 'none', border: 'none', cursor: 'pointer',
	      fontSize: 13, color: '#64748b', fontFamily: 'inherit',
	      display: 'flex', alignItems: 'center', gap: 6,
	      padding: 0, transition: 'color 0.2s',
	    }}
	    onMouseEnter={e => e.currentTarget.style.color = '#6366f1'}
	    onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
	  >
	    ← Back to Home
	  </button>
	</div>

        {/* Header */}
        <div style={{ padding: '40px 40px 32px', textAlign: 'center', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{
		  width: 64, height: 64, borderRadius: 18,
		  background: '#f8fafc',
		  display: 'flex', alignItems: 'center', justifyContent: 'center',
		  margin: '0 auto 20px',
		  boxShadow: '0 8px 20px rgba(99,102,241,0.15)',
		  border: '1px solid #e2e8f0',
		}}>
		  <img src="/favicon.svg" alt="Logo" style={{ width: 40, height: 40, objectFit: 'contain' }} />
	</div>
          <h1 style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 28, fontWeight: 400,
            color: '#0f172a', margin: '0 0 6px',
            letterSpacing: '-0.3px',
          }}>
            Super Admin
          </h1>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
            Platform management portal
          </p>
        </div>

        {/* Form */}
        <div style={{ padding: '32px 40px 40px' }}>

          {/* General error */}
          {errors.general && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, padding: '12px 16px', marginBottom: 20,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>

            {/* Email */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={e => { setForm(p => ({ ...p, email: e.target.value })); if (errors.email) setErrors(p => ({ ...p, email: undefined })); }}
                placeholder="super@platform.com"
                style={inputStyle('email')}
                onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = errors.email ? '#ef4444' : '#e2e8f0'; e.target.style.background = errors.email ? '#fef2f2' : '#f8fafc'; e.target.style.boxShadow = 'none'; }}
              />
              {errors.email && (
                <p style={{ fontSize: 12, color: '#ef4444', marginTop: 6, margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                  ✕ {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div style={{ marginBottom: 28 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => { setForm(p => ({ ...p, password: e.target.value })); if (errors.password) setErrors(p => ({ ...p, password: undefined })); }}
                  placeholder="••••••••"
                  style={{ ...inputStyle('password'), paddingRight: 48 }}
                  onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = errors.password ? '#ef4444' : '#e2e8f0'; e.target.style.background = errors.password ? '#fef2f2' : '#f8fafc'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  type="button"
                  className="show-pass"
                  onClick={() => setShowPass(s => !s)}
                  style={{
                    position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 16, color: '#94a3b8', padding: 0, transition: 'color 0.2s',
                  }}
                >
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && (
                <p style={{ fontSize: 12, color: '#ef4444', margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                  ✕ {errors.password}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="login-btn"
              disabled={loading}
              style={{
                width: '100%', padding: '14px',
                background: loading ? '#a5b4fc' : '#6366f1',
                color: '#fff', border: 'none', borderRadius: 12,
                fontSize: 15, fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading ? 'none' : '0 4px 12px rgba(99,102,241,0.3)',
                fontFamily: 'inherit',
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)',
                    borderTop: '2px solid #fff', borderRadius: '50%',
                    display: 'inline-block', animation: 'spin 0.7s linear infinite',
                  }} />
                  Signing in...
                </>
              ) : 'Sign In →'}
            </button>
          </form>

          {/* Footer note */}
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
              🔒 Restricted access — Super Admins only
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
